import { Component, inject, Input, OnInit, AfterViewInit, signal } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { finalize } from 'rxjs';
import { BaseModalComponent, EnumValues } from '@card-software/library';
import { MessageService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { LabDip } from '../../../models/lab-dip/lab-dip';
import { LabDipProxyService } from '../../../proxies/item/lab-dip.proxy.service';
import { EnumService } from '../../../services/enum.service';
import { ShadeProxyService } from '../../../proxies/item/shade.proxy.service';
import { Shade } from '../../../models/shade/shade';
import { FabricProxyService } from '../../../proxies/item/fabric.proxy.service';
import { LabDipRequestProxyService } from '../../../proxies/item/lab-dip-request.proxy.service';
import { Fabric } from '../../../models/fabric/fabric';
import { YarnProxyService } from '../../../proxies/item/yarn.proxy.service';
import { Yarn } from '../../../models/yarns/yarn';
import { RecipeProxyService } from '../../../proxies/item/recipe.proxy.service';
import { Recipe } from '../../../models/recipe/recipe';

@Component({
  selector: 'app-lab-dip-details-modal',
  templateUrl: './lab-dip-details-modal.component.html',
  styleUrl: './lab-dip-details-modal.component.scss',
})
export class LabDipDetailsModalComponent extends BaseModalComponent<LabDip | null> implements OnInit, AfterViewInit {
  private labDipProxyService = inject(LabDipProxyService);
  private messageService = inject(MessageService);
  private translateService = inject(TranslateService);
  private shadeProxyService = inject(ShadeProxyService);
  private fabricProxyService = inject(FabricProxyService);
  private yarnProxyService = inject(YarnProxyService);
  private labDipRequestProxyService = inject(LabDipRequestProxyService);
  private recipeProxyService = inject(RecipeProxyService);

  private enumService = inject(EnumService);

  yarns: Yarn[] = [];

  shadeOptions: { label: string; value: string }[] = [];

  @Input() canEdit = false;
  @Input() id: string | null = null;

  isLoading = signal(0);

  fabrics: Fabric[] = [];

  mainForm: FormGroup = this.initializeMainForm();
  labDip = signal<LabDip | null>(null);
  existingRecipe = signal<Recipe | null>(null);
  labDipRecipes = signal<Recipe[]>([]);

  private _convertShadesToOptions(shades: Shade[]): { label: string; value: string }[] {
    return shades.map((shade) => ({
      label: shade.name,
      value: shade.id || '',
    }));
  }

  private _setOptionsForPrimeNg(enumValues: EnumValues): { label: string; value: number }[] {
    return Object.keys(enumValues)
      .filter((key) => !isNaN(Number(key)))
      .map((key) => ({
        label: enumValues[parseInt(key)],
        value: parseInt(key),
      }));
  }

  private initializeMainForm(): FormGroup {
    return new FormGroup({
      labDipReference: new FormControl<string | null>(null, [Validators.required]),
      recipeCharacter: new FormControl<string | null>(null, [Validators.required]),
      shadeReference: new FormControl<string | null>(null, [Validators.required]),
    });
  }

  getFabrics() {
    const fabricCodes = this.labDip()?.labDipRequest?.fabricCodes;
    if (!fabricCodes || fabricCodes.length === 0) {
      return;
    }
    this.fabricProxyService.batchGetFabricsByCodes(fabricCodes).subscribe({
      next: (response) => {
        if (response.data) {
          this.fabrics = response.data;
          this.getDistinctYarns(this.fabrics);
        }
      },
    });
  }

  getDistinctYarns(fabrics: Fabric[]) {
    const yarnSet = new Set<string>();
    //trverse thru all the fabric.greige.yarnDetails and extract hte yarn code
    fabrics.forEach((fabric) => {
      fabric.greige?.yarnDetails?.forEach((yarnDetail) => {
        if (yarnDetail.yarn && yarnDetail.yarn.code) {
          yarnSet.add(yarnDetail.yarn.code);
        }
      });
    });
    const yarnCodes = Array.from(yarnSet);
    yarnCodes.forEach((yarnCode) => {
      this.yarnProxyService.getYarnByCode(yarnCode).subscribe({
        next: (response) => {
          if (response.data?.items?.length) {
            this.yarns.push(response.data.items[0]);
          }
        },
      });
    });
  }

  startLoading(): void {
    this.isLoading.update((val) => val + 1);
  }

  stopLoading(): void {
    this.isLoading.update((val) => Math.max(0, val - 1));
  }

  ngOnInit(): void {
    const id = this.id;
    if (id !== null) {
      this.mainForm.patchValue({ labDipReference: id });
      this.loadById(id);
    }

    this.shadeProxyService.getAllShades().subscribe({
      next: (response) => {
        if (response.data) {
          this.shadeOptions = this._convertShadesToOptions(response.data);
        }
      },
    });
  }

  ngAfterViewInit(): void {
    if (!this.canEdit) {
      this.mainForm.disable();
    }
  }

  private loadById(id: string) {
    this.startLoading();
    this.labDipProxyService
      .getLabDipById(id)
      .pipe(
        finalize(() => {
          this.stopLoading();
        }),
      )
      .subscribe({
        next: (response) => {
          console.log(response);

          if (response.data) {
            this.labDip.set(response.data);
            this.getFabrics();
            this.loadExistingRecipe(id);
          }
        },
      });
  }

  private loadExistingRecipe(labDipReference: string) {
    this.startLoading();
    this.recipeProxyService
      .getRecipesByLabDipReference(labDipReference)
      .pipe(
        finalize(() => {
          this.stopLoading();
        }),
      )
      .subscribe({
        next: (response) => {
          const recipes = response.data ?? [];
          this.labDipRecipes.set(recipes);
          if (recipes.length > 0) {
            const recipe = this.findRecipeByNumber(recipes, this.labDip()?.colorDto?.approvedRecipeNumber) ?? this.newestRecipe(recipes);
            this.existingRecipe.set(recipe);

            // Extract recipe character from recipe number
            // Recipe number format: "LAB-XXXXX-Y" where Y is the recipe character
            const recipeNumberParts = recipe.recipeNumber.split('-');
            const recipeCharacter = recipeNumberParts.length > 1 ? recipeNumberParts[recipeNumberParts.length - 1] : '';

            // Populate form with existing data
            this.mainForm.patchValue({
              recipeCharacter: recipeCharacter,
              shadeReference: recipe.shadeReference,
            });
          }
        },
        error: (error) => {
          console.error('Error loading existing recipe:', error);
        },
      });
  }

  private findRecipeByNumber(recipes: Recipe[], recipeNumber: string | null | undefined): Recipe | undefined {
    const wanted = recipeNumber?.trim().toLowerCase();
    return wanted ? recipes.find((recipe) => recipe.recipeNumber?.trim().toLowerCase() === wanted) : undefined;
  }

  private newestRecipe(recipes: Recipe[]): Recipe {
    return recipes.reduce((newest, recipe) =>
      new Date(recipe.createdDate ?? 0).getTime() > new Date(newest.createdDate ?? 0).getTime() ? recipe : newest,
    );
  }

  canSave(): boolean {
    return this.mainForm.valid && this.isLoading() === 0;
  }

  onSave() {
    if (this.mainForm.valid) {
      const recipeNumber = `${this.labDip()?.labDipNumber?.trim() ?? ''}-${this.mainForm.value.recipeCharacter?.trim() ?? ''}`;
      const isUpdate = this.findRecipeByNumber(this.labDipRecipes(), recipeNumber) !== undefined;
      this.startLoading();

      this.labDipProxyService
        .createRecipe(this.mainForm.value)
        .pipe(
          finalize(() => {
            this.stopLoading();
          }),
        )
        .subscribe({
          next: (result) => {
            if (result.success) {
              const messageKey = isUpdate ? 'LabDips_RecipeUpdated' : 'LabDips_RecipeCreated';
              const defaultMessage = isUpdate ? 'Recipe updated successfully' : 'Recipe created successfully';

              this.messageService.add({
                severity: 'success',
                summary: this.translateService.instant('Common_Success'),
                detail: this.translateService.instant(messageKey) || defaultMessage,
              });

              this.close({ action: 'success', data: this.labDip() });
            }
          },
        });
    }
  }

  onClose() {
    this.close({ action: 'close', data: null });
  }
}
